import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  List, ListItem, ListItemText, CircularProgress,
} from '@mui/material';
import { Warning, DeleteForever } from '@mui/icons-material';
import api from '../../../services/api';

const DeleteUser: React.FC = () => {
  const [email, setEmail] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data } = await api.post('/admin/users/emergency-delete', { email: email.trim() });
      if (data.success) {
        setResult(data);
      } else {
        setError(data.error || 'Erro desconhecido');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Erro na conexão');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <DeleteForever color="error" fontSize="large" />
        Eliminar Utilizador (Emergência)
      </Typography>

      <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
        <strong>Atenção:</strong> Esta operação é <strong>irreversível</strong>. Apaga o utilizador permanentemente
        da base de dados, incluindo:
        <List dense sx={{ pl: 2, pt: 1 }}>
          <ListItem sx={{ py: 0 }}><ListItemText primary="• Pre-registos e OTPs" /></ListItem>
          <ListItem sx={{ py: 0 }}><ListItemText primary="• Wallet e transações" /></ListItem>
          <ListItem sx={{ py: 0 }}><ListItemText primary="• Pontos de fidelidade e referidos" /></ListItem>
          <ListItem sx={{ py: 0 }}><ListItemText primary="• Notificações e referências guardadas" /></ListItem>
          <ListItem sx={{ py: 0 }}><ListItemText primary="• Cartões SISP tokenizados" /></ListItem>
          <ListItem sx={{ py: 0 }}><ListItemText primary="• Pagamentos SISP históricos" /></ListItem>
          <ListItem sx={{ py: 0 }}><ListItemText primary="• Cliente, entidade e conta Firebase" /></ListItem>
        </List>
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Dados do utilizador a eliminar</Typography>

        <TextField
          fullWidth
          label="Email ou Username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="exemplo@dominio.com"
          sx={{ mb: 2, mt: 2 }}
          disabled={loading}
        />

        <TextField
          fullWidth
          label="Chave de segurança"
          type="password"
          value={secretKey}
          onChange={(e) => setSecretKey(e.target.value)}
          placeholder="Chave fornecida ao admin"
          helperText="Contacta o desenvolvedor se não tens a chave."
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Button
          variant="contained"
          color="error"
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteForever />}
          onClick={() => setConfirmOpen(true)}
          disabled={!email.trim() || !secretKey.trim() || loading}
          fullWidth
        >
          {loading ? 'A eliminar...' : 'Eliminar Permanentemente'}
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Erro:</strong> {error}
        </Alert>
      )}

      {result && (
        <Paper sx={{ p: 3, bgcolor: 'success.light' }}>
          <Typography variant="h6" gutterBottom color="success.dark">
            ✓ Operação concluída
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Users eliminados: <strong>{result.deletedUsers?.length || 0}</strong>
          </Typography>
          {result.deletedUsers?.map((id: string) => (
            <Typography key={id} variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
              {id}
            </Typography>
          ))}
          {result.log && result.log.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#111827', color: '#E5E7EB', borderRadius: 1, fontFamily: 'monospace', fontSize: 12, maxHeight: 300, overflow: 'auto' }}>
              {result.log.map((l: string, i: number) => (
                <div key={i}>{l}</div>
              ))}
            </Box>
          )}
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" /> Confirmar Eliminação Permanente
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vais eliminar permanentemente o utilizador <strong>{email}</strong> e <strong>todos os dados associados</strong>.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
            <strong>Esta ação não pode ser desfeita.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" startIcon={<DeleteForever />}>
            Sim, Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeleteUser;
